import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { Form, Params, useActionData, useNavigate } from "react-router-dom";
import { AppContext } from "../App"
import { API_URL } from "../lib/config";

const errors = {
    invalid_user: "Usuário inválido",
    invalid_password: "Senha inválida",
    unauthorized: "Não autorizado",
    connection_error: "Erro de conexão",
};

type Error = keyof typeof errors;

export async function action({
    request,
    params,
}: {
    request: Request;
    params: Params<string>;
}) {
    const actionData: { ok: boolean; auth: string | null; errors: Error[] } = {
        ok: true,
        auth: null,
        errors: [],
    };
    const formData = await request.formData();

    const user = formData.get("user");
    const password = formData.get("password");

    if (!user) {
        actionData.errors.push("invalid_user");
    }

    if (!password) {
        actionData.errors.push("invalid_password");
    }

    if (actionData.errors.length === 0) {
        try {
            const auth = btoa(`${user}:${password}`);
            const response = await fetch(API_URL + "/login", {
                headers: {
                    Authorization: "Basic " + auth,
                },
            });

            if (response.ok) {
                actionData.auth = auth;
            } else {
                if (response.status === 401) {
                    actionData.errors.push("unauthorized");
                } else {
                    actionData.errors.push("connection_error");
                }
            }
        } catch (error) {
            console.error(error);
            actionData.errors.push("connection_error");
        }
    }

    if (actionData.errors.length > 0) actionData.ok = false;

    return actionData;
}

export default function Login() {
    const appContext = useContext(AppContext);

    const navigate = useNavigate();
    const actionData = useActionData() as
        | Awaited<ReturnType<typeof action>>
        | undefined;

    const [currentErrors, setCurrentErrors] = useState<Error[]>([]);

    useEffect(() => {
        if (actionData) {
            setCurrentErrors(actionData.errors);
            if (appContext && actionData.ok && actionData.auth) {
                appContext.setAuth(actionData.auth);
            }
        }
    }, [appContext, actionData]);

    useEffect(() => {
        if (appContext && appContext.auth) {
            navigate("/dashboard");
        }
    }, [appContext]);

    function clearErrors() {
        setCurrentErrors([]);
    }

    return (
        <div className="mt-8 flex w-11/12 max-w-sm flex-col items-center gap-6 rounded-md border border-xv-border-color bg-xv-bg-2 pt-8 pb-12">
            <h2 className="px-2 py-1 text-3xl">Fazer Login</h2>
            <Form
                className="flex w-10/12 flex-col items-center gap-10"
                method="post"
            >
                <input
                    onChange={clearErrors}
                    className={classNames(
                        "w-full border-b border-xv-border-color bg-transparent px-2 py-1 outline-none transition-all focus:border-white focus:outline-none",
                        {
                            "!border-red-500 text-red-500":
                                currentErrors.includes("unauthorized") ||
                                currentErrors.includes("invalid_user"),
                        }
                    )}
                    required
                    type="text"
                    name="user"
                    placeholder="usuario"
                />
                <input
                    onChange={clearErrors}
                    className={classNames(
                        "w-full border-b border-xv-border-color bg-transparent px-2 py-1 outline-none transition-all focus:border-white focus:outline-none",
                        {
                            "!border-red-500 text-red-500":
                                currentErrors.includes("unauthorized") ||
                                currentErrors.includes("invalid_password"),
                        }
                    )}
                    required
                    type="password"
                    name="password"
                    placeholder="********"
                />
                <button
                    className="mt-4 w-fit rounded-md bg-xv-red px-12 py-2 font-medium hocus:brightness-90"
                    type="submit"
                >
                    Entrar
                </button>
            </Form>
            <div className="flex flex-col gap-2 text-center text-sm font-medium text-red-500">
                {currentErrors.map((error, i) => (
                    <div key={i}>{errors[error]}</div>
                ))}
            </div>
        </div>
    );
}
